// Auth store — JWT tokens + user identity + permission manifest.

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type User = {
  id: number;
  username: string;           // primary login identifier — always set
  email: string;
  name: string;
  mfa_enabled: boolean;
  status: string;
  roles: string[];
  last_login_at?: string | null;
};

export type Manifest = {
  roles: string[];
  permissions: string[];
  menus: Record<string, boolean>;
  menu_manage?: Record<string, boolean>;
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  manifest: Manifest | null;

  setTokens: (access: string, refresh: string) => void;
  setUser: (u: User | null) => void;
  setManifest: (m: Manifest | null) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  hasRole: (role: string) => boolean;
  can: (perm: string) => boolean;
  canAccessMenu: (key: string) => boolean;
  canManageMenu: (key: string) => boolean;
};

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      manifest: null,
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setUser: (user) => set({ user }),
      setManifest: (manifest) => set({ manifest }),
      logout: () => set({ accessToken: null, refreshToken: null, user: null, manifest: null }),
      isAuthenticated: () => !!get().accessToken,
      hasRole: (role) => get().manifest?.roles.includes(role) ?? get().user?.roles.includes(role) ?? false,
      can: (perm) => get().manifest?.permissions.includes(perm) ?? false,
      // A menu is accessible/manageable when its manifest flag is true.
      // Unknown keys default to accessible (legacy menus without config).
      canAccessMenu: (key) => get().manifest?.menus?.[key] !== false,
      canManageMenu: (key) => get().manifest?.menu_manage?.[key] === true,
    }),
    {
      name: "timple-auth",
      // Hydration tracking is done via useAuth.persist.hasHydrated() and
      // useAuth.persist.onFinishHydration() in ProtectedRoute — no need for
      // a manual _hydrated state field.
    },
  ),
);
